import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Loader2, Users, Home, Plane } from 'lucide-react';

import { LocationComboBox } from '../LocationComboBox';
import { GAME_CATEGORIES } from './constants';

/**
 * Edit Game dialog (iter279 extraction from TournamentGames.js).
 * Edits date/time/reporting/RSVP/category/opponent/location/venue/notes
 * + lets the admin reassign the game's team within the same age category.
 */
const EditGameDialog = ({
  open,
  onOpenChange,
  selectedGame,
  formData,
  setFormData,
  teams,
  onUpdate,
  submitting,
}) => {
  const ageCategory = selectedGame?.age_category;
  const teamsForCategory = teams.filter(t => t.age_category === ageCategory);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Game</DialogTitle>
          <DialogDescription>
            Update game details for {ageCategory?.toUpperCase()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Team Assignment */}
          {ageCategory && (
            <div className="bg-slate-50 rounded-lg p-3 border">
              <Label className="text-sm font-medium text-slate-600 mb-2 block">
                Assigned Team ({ageCategory?.toUpperCase()})
              </Label>
              {teamsForCategory.length === 0 ? (
                <p className="text-sm text-amber-600 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  No teams created for this category yet
                </p>
              ) : (
                <div className="space-y-2">
                  <div
                    className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                      !formData.team_id
                        ? 'bg-blue-100 border border-blue-300'
                        : 'bg-white border hover:bg-slate-100'
                    }`}
                    onClick={() => setFormData({ ...formData, team_id: '' })}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={!formData.team_id}
                        onChange={() => setFormData({ ...formData, team_id: '' })}
                        className="h-4 w-4 text-blue-600"
                      />
                      <span className="font-medium text-slate-500">No Team (Open to all)</span>
                    </div>
                  </div>
                  {teamsForCategory.map(team => (
                    <div
                      key={team.id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                        formData.team_id === team.id
                          ? 'bg-blue-100 border border-blue-300'
                          : 'bg-white border hover:bg-slate-100'
                      }`}
                      onClick={() => setFormData({ ...formData, team_id: team.id })}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={formData.team_id === team.id}
                          onChange={() => setFormData({ ...formData, team_id: team.id })}
                          className="h-4 w-4 text-blue-600"
                        />
                        <div>
                          <span className="font-medium">{team.name}</span>
                          {team.division && (
                            <Badge variant="outline" className="ml-2 text-xs">{team.division}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-slate-500">
                        {team.player_count} player{team.player_count !== 1 ? 's' : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Game Date</Label>
              <Input
                type="date"
                value={formData.game_date}
                onChange={(e) => setFormData({ ...formData, game_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Start Time</Label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Reporting Time</Label>
              <Input
                type="time"
                value={formData.reporting_time}
                onChange={(e) => setFormData({ ...formData, reporting_time: e.target.value })}
                placeholder="When players should arrive"
              />
              <p className="text-xs text-slate-500 mt-1">When players should arrive</p>
            </div>
            <div>
              <Label>RSVP Deadline</Label>
              <Input
                type="datetime-local"
                value={formData.rsvp_deadline}
                onChange={(e) => setFormData({ ...formData, rsvp_deadline: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">RSVPs close after this time</p>
            </div>
          </div>

          <div>
            <Label>Game Category</Label>
            <Select
              value={formData.game_category}
              onValueChange={(value) => setFormData({ ...formData, game_category: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {GAME_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Opponent Team</Label>
            <Input
              value={formData.opponent_team}
              onChange={(e) => setFormData({ ...formData, opponent_team: e.target.value })}
            />
          </div>

          <div>
            <Label>Location</Label>
            <LocationComboBox
              value={formData.location}
              locationUrl={formData.location_url}
              onChange={(val) => setFormData(prev => ({ ...prev, location: val }))}
              onLocationUrlChange={(val) => setFormData(prev => ({ ...prev, location_url: val }))}
            />
          </div>

          <div>
            <Label>Venue Type</Label>
            <Select
              value={formData.game_type}
              onValueChange={(value) => setFormData({ ...formData, game_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="home">
                  <div className="flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Home
                  </div>
                </SelectItem>
                <SelectItem value="away">
                  <div className="flex items-center gap-2">
                    <Plane className="h-4 w-4" />
                    Away
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Notes (Optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional details about the game..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onUpdate} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Update Game
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditGameDialog;
